import { useState, useEffect } from "react"
import { gapi } from "gapi-script"
import MapBody from "../MapBody/MapBody"
import { goggleAuthClientID } from "../../appsettings"
import { getAnonymousUserID, getResearchID, getScreenType } from "../../utils"
import { addActivity, getProjectModeConfigForUser } from "../../apiCalls"
import { ACTIVITY_TYPES, PROJECT_MODES } from "../../constants"
import NavigationBar from "../NavigationBar/NavigationBar"
import NotesModal from "../NotesModal/NotesModal"
import FloatingNotesButton from '../FloatingNotesButton/FloatingNotesButton'
import Toolbar from '../Toolbar/Toolbar'
import GoogleLogin from "react-google-login"
import SaveToLibraryButton from '../SaveToLibraryButton/SaveToLibraryButton'

const Login = () => {

	useEffect(() => {
			const initClient = () => {
				gapi.client.init({
					clientID: clientID,
					scope: ""
				})
			}
			gapi.load("client:auth2", initClient)
	})

	useEffect(() => {
		const handleTabClose = async () => {
			await addActivity(
				anonymousUserId,
				researchId,
				ACTIVITY_TYPES.TAB_CLOSE,
				null,
				null,
				null,
				null,
				projectMode,
				getScreenType()
			)
		}

		const handleTabChange = async (event) => {
			if(document.hidden === true){
				await addActivity(
					anonymousUserId,
					researchId,
					ACTIVITY_TYPES.TAB_CHANGE,
					null,
					null,
					null,
					null,
					projectMode,
					getScreenType()
				)
			}
			
		}

		window.addEventListener('visibilitychange', handleTabChange)
		window.addEventListener('unload', handleTabClose)
		return () => {
			window.removeEventListener('unload', handleTabClose)
			window.removeEventListener('visibilitychange', handleTabChange)
		}
	})

	useEffect(() => {
		// Prevent zooming using Ctrl/Cmd + scroll or pinch gestures
		const handleWheel = (e) => {
			if (e.ctrlKey || e.metaKey) {
				e.preventDefault();
			}
		};

		const handleKeyDown = (e) => {
			// Prevent zooming using Ctrl/Cmd + +/- keys
			if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
				e.preventDefault();
			}
		};

		document.addEventListener('wheel', handleWheel, { passive: false });
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('wheel', handleWheel);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	// new client id
	const clientID = goggleAuthClientID

	const [userProfile, setUserProfile] = useState()
	const [anonymousUserId, setAnonymousUserId] = useState()
	const [researchId, setResearchId] = useState()
	const [projectMode, setProjectMode] = useState(PROJECT_MODES.INTERACTIVE_MAPS_MODE)
	const [displayModal, setDisplayModal] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [showReuploadControls, setShowReuploadControls] = useState(false)
	const [showResizeControls, setShowResizeControls] = useState(false)
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [hasUploadedMap, setHasUploadedMap] = useState(false)
	
	// Add this effect to monitor state changes
	useEffect(() => {
		console.log('Edit mode changed:', isEditMode);
	}, [isEditMode]);

	const onAuthSuccess = async (res) => {
		setIsAuthenticated(true)
		setUserProfile(res.profileObj)
		const encryptedUserID = await getAnonymousUserID(res.profileObj)
		setAnonymousUserId(encryptedUserID)
		const generatedResearchId = getResearchID(encryptedUserID)
		setResearchId(generatedResearchId)
		const tempProjectMode = await getProjectModeConfigForUser(res.profileObj.email)
		setProjectMode(tempProjectMode)
		await addActivity(
			encryptedUserID,
			generatedResearchId,
			ACTIVITY_TYPES.LOGIN,
			null,
			null,
			null,
			null,
			tempProjectMode,
			getScreenType()
		)
		
		// Store data in localStorage
		localStorage.setItem('userProfile', JSON.stringify(res.profileObj))
		localStorage.setItem('anonymousUserId', encryptedUserID)
		localStorage.setItem('researchId', generatedResearchId)
	}

	const onAuthFailure = (err) => {
		console.log("failure: ", err)
	}

	const logOut = async (activity_type = ACTIVITY_TYPES.LOGOUT) => {
		await addActivity(
			anonymousUserId,
			researchId,
			activity_type,
			null,
			null,
			null,
			null,
			projectMode,
			getScreenType()
		)
		setUserProfile(null)
		setAnonymousUserId(null)
		setResearchId(null)
		setIsAuthenticated(false)
	}

	// Update the toggle handler
	const handleEditToggle = () => {
		console.log('Toggling edit mode');
		setIsEditMode(prev => !prev);
	};

	const handleExport = async (canvasId) => {
		// Get references to the canvases
		const leftCanvas = document.querySelector('canvas:first-of-type');
		const rightCanvas = document.querySelector('canvas:last-of-type');
		
		if (!leftCanvas || !rightCanvas) {
			console.error('Canvases not found');
			return null;
		}

		// Create a temporary canvas for combining both canvases
		const tempCanvas = document.createElement('canvas');
		const tempCtx = tempCanvas.getContext('2d');

		if (canvasId === 'both') {
			const width = leftCanvas.width;
			const height = leftCanvas.height;
			const margin = 240;  // Add 240px margin on each side
			
			// Set dimensions to fit both canvases vertically with margins
			tempCanvas.width = width + (margin * 2);
			tempCanvas.height = height * 2 + 8;
			
			// Fill with white background
			tempCtx.fillStyle = 'white';
			tempCtx.fillRect(0, 0, width + (margin * 2), height * 2 + 20);
			
			// Draw both canvases vertically with margins
			tempCtx.drawImage(leftCanvas, margin, 0);
			tempCtx.drawImage(rightCanvas, margin, height + 20);
			
			// Add separator line
			tempCtx.strokeStyle = '#e0e0e0';
			tempCtx.lineWidth = 1;
			tempCtx.beginPath();
			tempCtx.moveTo(0, height + 10);
			tempCtx.lineTo(width + (margin * 2), height + 10);
			tempCtx.stroke();
		} else {
			// Export single canvas
			const sourceCanvas = canvasId === 'left' ? leftCanvas : rightCanvas;
			const margin = 240;
			
			tempCanvas.width = sourceCanvas.width + (margin * 2);
			tempCanvas.height = sourceCanvas.height;
			
			// Fill with white background
			tempCtx.fillStyle = 'white';
			tempCtx.fillRect(0, 0, sourceCanvas.width + (margin * 2), sourceCanvas.height);
			
			// Draw the canvas with margins
			tempCtx.drawImage(sourceCanvas, margin, 0);
		}
		
		return tempCanvas.toDataURL('image/png');
	};

	return (
		<div>
			{!isAuthenticated ? (
				<div style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
					background: 'linear-gradient(to right bottom, #ffffff, #f5f5f5)',
					padding: '20px'
				}}>
					<div style={{
						textAlign: 'center',
						marginBottom: '40px'
					}}>
						<h1 style={{
							fontSize: '32px',
							color: '#333',
							marginBottom: '16px',
							fontWeight: '600'
						}}>
							Welcome to Map Resource
						</h1>
						<p style={{
							fontSize: '16px',
							color: '#666',
							maxWidth: '500px',
							lineHeight: '1.6',
							marginBottom: '16px'
						}}>
							Sign in with your Google account to access interactive maps and collaborative features.
						</p>
						<p style={{
							fontSize: '15px',
							color: '#e65100',
							maxWidth: '500px',
							lineHeight: '1.6',
							marginBottom: '32px',
							fontWeight: '500',
							background: 'rgba(230, 81, 0, 0.05)',
							padding: '12px',
							borderRadius: '8px',
							border: '1px solid rgba(230, 81, 0, 0.1)'
						}}>
							Notes will be automatically saved, but map images and annotations will not persist after refresh.
						</p>
					</div>
					<div style={{
						background: 'white',
						padding: '32px 40px',
						borderRadius: '16px',
						boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center'
					}}>
						<GoogleLogin 
							clientId={goggleAuthClientID}
							buttonText="Sign in with Google"
							onSuccess={onAuthSuccess}
							onFailure={onAuthFailure}
							cookiePolicy={"single_host_origin"}
							isSignedIn={true}
						/>
					</div>
				</div>
			) : (
				<div style={{
					display: 'grid',
					gridTemplateRows: 'auto 1fr',  // Remove the 80px row since toolbar will be fixed
					height: '100vh',
					maxHeight: '100vh',
					overflow: 'hidden',
					gap: '0',
					paddingTop: '64px'
				}}>
					<NavigationBar 
						userProfile={userProfile} 
						anonymousUserId={anonymousUserId}
						researchId={researchId} 
						onAuthSuccess={onAuthSuccess}
						onAuthFailure={onAuthFailure}
						logoutFunction={logOut}
						setDisplayModal={setDisplayModal}
						projectMode={projectMode}
						hasUploadedMap={hasUploadedMap}
					/>
					<MapBody 
						anonymousUserId={anonymousUserId} 
						researchId={researchId} 
						projectMode={projectMode}
						isEditMode={isEditMode}
						showReuploadControls={showReuploadControls}
						showResizeControls={showResizeControls}
						setHasUploadedMap={setHasUploadedMap}
						onExport={handleExport}
					/>
					
					{/* Fixed position container for bottom elements */}
					<div style={{
						position: 'fixed',
						bottom: '20px',
						left: '0',
						right: '0',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 1000,
						pointerEvents: 'none'  // This allows clicking through the container
					}}>
						<div style={{ 
							pointerEvents: 'auto'  // Re-enable pointer events for the toolbar
						}}>
							<Toolbar 
								onEditToggle={handleEditToggle}
								isEditing={isEditMode}
								showReuploadControls={showReuploadControls}
								showResizeControls={showResizeControls}
								setShowReuploadControls={setShowReuploadControls}
								setShowResizeControls={setShowResizeControls}
							/>
						</div>
					</div>

					{/* Fixed position for notes button */}
					<div style={{
						position: 'fixed',
						bottom: '20px',
						left: '20px',
						zIndex: 1000
					}}>
						<FloatingNotesButton 
							anonymousUserId={anonymousUserId}
							researchId={researchId}
							projectMode={projectMode}
							setDisplayModal={setDisplayModal}
						/>
					</div>

					<NotesModal
						displayModal={displayModal} 
						setDisplayModal={setDisplayModal} 
						anonymousUserId={anonymousUserId}
						researchId={researchId}
						projectMode={projectMode}
					/>
				</div>
			)}
		</div>
	)
}

export default Login